"use client";

import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

import { DEMO_SESSION_KEY, useDemoMode } from "@/components/demo/DemoModeProvider";
import { VolumeIcon } from "@/components/fun-ui/icons";
import { cn } from "@/lib/utils";
import { fetchCommonConfig } from "@/lib/common-config-client";

const ReactMarkdown = dynamic(() => import("react-markdown"), { ssr: false });

type SystemAnnouncementItem = {
  content: string;
  linkto?: string;
  linkTo?: string;
  pages: string[];
};

const barClassName =
  "flex flex-row h-10 max-h-10 bg-[#F8E9E2] items-center justify-center gap-2 text-[#FF6700]";

function buildAnnouncementPageKey(pathname: string): string {
  const regex = /^pages\/([^/]+(?:\/[^/]+)*)\/index(?:\?[^#]*)?(?:#.*)?$/;
  const match = pathname.match(regex);
  if (match?.[1]) {
    return `wx:${match[1]}`;
  }
  return `pc:${pathname.replace(/^\//, "") || "default"}`;
}

function matchAnnouncementPage(pageKey: string, page: string): boolean {
  const normalized = page.trim();
  return Boolean(normalized) && (pageKey === normalized || pageKey.startsWith(`${normalized}/`));
}

function resolveAnnouncementItem(
  items: SystemAnnouncementItem[],
  pageKey: string,
): SystemAnnouncementItem | null {
  let matched: SystemAnnouncementItem | null = null;
  items.forEach((item) => {
    const pages = Array.isArray(item.pages) ? item.pages : [];
    if (pages.some((page) => matchAnnouncementPage(pageKey, String(page)))) {
      matched = item;
    }
  });
  return matched;
}

export function LandingSystemAnnouncement() {
  const pathname = usePathname();
  const demoFromProvider = useDemoMode();
  const [demoFromLocation, setDemoFromLocation] = useState(false);
  const [announcement, setAnnouncement] = useState<{
    content: string;
    linkto: string;
  } | null>(null);
  const demo = demoFromProvider || demoFromLocation;

  useEffect(() => {
    const demoParam = new URLSearchParams(window.location.search).get("demo");
    if (demoParam === "true") {
      setDemoFromLocation(true);
      return;
    }
    if (demoParam === "false") {
      setDemoFromLocation(false);
      return;
    }
    try {
      setDemoFromLocation(sessionStorage.getItem(DEMO_SESSION_KEY) === "1");
    } catch {
      setDemoFromLocation(false);
    }
  }, [pathname]);

  useEffect(() => {
    if (demo) {
      setAnnouncement(null);
      return;
    }

    let cancelled = false;
    void fetchCommonConfig<SystemAnnouncementItem[]>("system_announcement", { silent: true }).then((items) => {
      if (cancelled) {
        return;
      }
      const matched = resolveAnnouncementItem(items || [], buildAnnouncementPageKey(pathname));
      const content = matched?.content?.trim() || "";
      const linktoRaw = matched?.linkto ?? matched?.linkTo;
      const linkto = typeof linktoRaw === "string" ? linktoRaw.trim() : "";
      setAnnouncement(content ? { content, linkto } : null);
    });

    return () => {
      cancelled = true;
    };
  }, [demo, pathname]);

  if (!announcement) {
    return null;
  }

  const body = (
    <>
      <VolumeIcon className="shrink-0" />
      {announcement.linkto ? (
        <span className="text-sm truncate">{announcement.content}</span>
      ) : (
        <ReactMarkdown>{announcement.content}</ReactMarkdown>
      )}
    </>
  );

  const isExternal = /^https?:\/\//i.test(announcement.linkto);

  return (
    <div
      className={cn(
        barClassName,
        "relative z-[5] shrink-0 grow-0 overflow-hidden pointer-events-none",
      )}
    >
      {announcement.linkto ? (
        <a
          href={announcement.linkto}
          className="inline-flex max-w-full items-center justify-center gap-2 cursor-pointer no-underline hover:opacity-90 pointer-events-auto"
          {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
          {body}
        </a>
      ) : (
        <div className="pointer-events-auto inline-flex max-w-full items-center justify-center gap-2">
          {body}
        </div>
      )}
    </div>
  );
}
